/**
 * The order line items read in, on an estimate or an invoice (#1441).
 *
 * Two separate things live here, and keeping them separate is the point:
 *
 *  - `moveLine` is the MANUAL move — the grab handle. It does what it is told.
 *  - `tidyLineOrder` is the RULE — group each device's work together, labor
 *    above the materials. It runs when the office presses "Tidy up", and never
 *    on its own.
 *
 * Corbin's words when he chose between the two (2026-09-09): a button, "but i
 * don't want it a hard rule to where i cannot delete a part off of the
 * estimate/invoice". So the rule is a thing you ask for, not a thing that
 * happens to you, and nothing in this file blocks any other edit. A line the
 * rule cannot classify is left exactly where it sits rather than guessed at —
 * see `isPinned`.
 */

/** The shape both estimate lines and invoice lines already arrive in. */
export interface OrderableLine {
    id: string;
    /** The device this line quotes. NULL = belongs to the job, not a device. */
    asset_id?: string | null;
    /**
     * The pricing engine's kind: 'labor' | 'material' | 'test_fee' | 'service' |
     * 'fee' | 'discount'. Present on any line that came from the pricebook;
     * NULL on a line somebody typed by hand.
     */
    pricebook_kind?: string | null;
    /** Per-device repair option (#1120) this line is priced under, if any. */
    option_id?: string | null;
    /** A person added or changed this line (migration 716). Tidy up never moves it. */
    user_touched?: boolean | null;
}

/**
 * Reading order within one device's block, and the reason for it: you do the
 * labor, you fit the parts, you test it afterwards, and the paperwork fees land
 * last. A customer reads it top to bottom as the sequence of the visit.
 */
const KIND_RANK: Readonly<Record<string, number>> = {
    labor: 0,
    service: 1,
    material: 2,
    part: 2, // what pricebook_items.kind calls a material
    test_fee: 3,
    fee: 4,
    discount: 5,
};

/** Where this line sorts inside its group, or null if we cannot tell. */
export function kindRank(line: OrderableLine): number | null {
    const kind = line.pricebook_kind?.trim().toLowerCase();
    if (!kind) return null;
    const rank = KIND_RANK[kind];
    return rank === undefined ? null : rank;
}

/**
 * A line Tidy up must not move.
 *
 * Three reasons, and all are promises rather than shortcuts:
 *
 *  1. **No kind to sort on.** A hand-typed line has no `pricebook_kind`, so any
 *     position the rule picked for it would be a guess dressed up as a decision.
 *     It keeps the slot it is in.
 *  2. **It is priced under a repair option (#1120).** Those lines belong to a
 *     choice the customer is being offered; re-sequencing across that boundary
 *     changes what "Complete rebuild" contains. Tidy up leaves options alone
 *     entirely — the per-device screen is already grouped by device by
 *     construction.
 *  3. **A person added or changed it** (Corbin 10/28, quote rebuild slice C).
 *     It is theirs, and that includes where they put it.
 */
export function isPinned(line: OrderableLine): boolean {
    if (line.user_touched) return true;
    if (line.option_id) return true;
    return kindRank(line) === null;
}

/**
 * Group by device, labor above the materials — the "Tidy up" rule.
 *
 * Returns the lines in their new reading order. Pure and total: same input,
 * same output, no mutation of the array it is handed.
 *
 * The shape of the result:
 *
 *  - **One block per device**, in the order those devices already appear. Tidy
 *    up never reshuffles which device comes first, so pressing it twice is the
 *    same as pressing it once, and the office's own device order survives.
 *  - **Lines with no device** (filing fees, trip charges) form a final block.
 *  - **Inside a block**, `KIND_RANK` decides; ties keep the order they had, so
 *    the #1 / #2 check order somebody set by hand is not thrown away.
 *  - **Pinned lines never move.** They hold their exact index and the movable
 *    lines fill the slots left over.
 */
export function tidyLineOrder<T extends OrderableLine>(lines: readonly T[]): T[] {
    const movable = lines.filter((l) => !isPinned(l));
    if (movable.length < 2) return [...lines];

    // Device blocks in order of first appearance; the no-device block last.
    const NO_DEVICE = " no-device";
    const blocks = new Map<string, T[]>();
    for (const line of movable) {
        const key = line.asset_id ?? NO_DEVICE;
        const block = blocks.get(key);
        if (block) block.push(line);
        else blocks.set(key, [line]);
    }
    const noDevice = blocks.get(NO_DEVICE);
    if (noDevice) {
        blocks.delete(NO_DEVICE);
        blocks.set(NO_DEVICE, noDevice);
    }

    const tidied: T[] = [];
    for (const block of blocks.values()) {
        // Array.prototype.sort is stable per spec, so equal ranks keep their
        // relative order and a hand-set #1-before-#2 survives the tidy.
        tidied.push(...[...block].sort((a, b) => (kindRank(a) ?? 0) - (kindRank(b) ?? 0)));
    }

    // Rebuild in place: pinned lines keep their slot, the tidied sequence fills
    // the rest. This is what "left exactly where it sits" means.
    let next = 0;
    return lines.map((line) => (isPinned(line) ? line : tidied[next++]));
}

/** True when Tidy up would change something — used to disable the button. */
export function needsTidying(lines: readonly OrderableLine[]): boolean {
    const tidied = tidyLineOrder(lines);
    return tidied.some((line, i) => line.id !== lines[i]?.id);
}

/**
 * Move one line to a new index — the grab handle, and the arrow keys.
 *
 * Deliberately dumb: it moves what it is asked to move. Whether a move is
 * ALLOWED (a locked document, crossing a repair option's boundary) is decided
 * by the caller and by the server, not smuggled in here.
 */
export function moveLine<T extends OrderableLine>(lines: readonly T[], lineId: string, toIndex: number): T[] {
    const from = lines.findIndex((l) => l.id === lineId);
    if (from === -1) return [...lines];
    const to = Math.max(0, Math.min(lines.length - 1, toIndex));
    if (from === to) return [...lines];
    const next = [...lines];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    return next;
}

/**
 * Whether a drop is allowed to land where it is aimed.
 *
 * A line priced under a repair option may be resequenced INSIDE that option,
 * but may not leave it: dragging it out would silently change what the customer
 * is agreeing to when they pick "Complete rebuild". Returns the refusal message
 * to show, or null when the move is fine.
 */
export function refuseCrossingOption(lines: readonly OrderableLine[], lineId: string, toIndex: number): string | null {
    const moved = lines.find((l) => l.id === lineId);
    if (!moved?.option_id) return null;
    const without = lines.filter((l) => l.id !== lineId);
    const to = Math.max(0, Math.min(without.length, toIndex));
    // Landing is inside the option when the line it would sit between belongs
    // to the same option. At either edge, one neighbour is enough.
    const before = without[to - 1]?.option_id ?? null;
    const after = without[to]?.option_id ?? null;
    const touchesOwnOption = before === moved.option_id || after === moved.option_id;
    return touchesOwnOption
        ? null
        : "That line belongs to a repair option. Move it inside the option, or remove the option first.";
}
